import {
    getSharePoolRemainingCount,
    isGroupSharePool,
    SharedMembershipParticipantSummary,
    SharedMembershipPool,
} from './types';

/**
 * 공유 수강권 화면의 핵심 질문은 "총 회차를 누가 얼마나 가져갔나"다.
 * 참여자 행 끝의 작은 숫자로는 3명만 넘어가도 비교가 안 되므로,
 * 하나의 막대에 쌓아 보여주기 위한 분해값을 여기서 계산한다.
 *
 * 회차는 서버에서 세 갈래로 나뉜다.
 *   capacity = usedCount(사용 완료) + reservedCount(예약·대기) + bookableRemaining(예약 가능)
 * 그룹 풀은 사람별로 집계하지 않으므로 사람 단위 분해 없이 총량만 낸다.
 */

/** 참여자 색. 막대 세그먼트와 참여자 행의 점이 같은 색이어야 범례 없이 읽힌다. */
export const SHARE_MEMBER_COLORS = [
    '#2B529D',
    '#129D8B',
    '#B4632D',
    '#7A4FBF',
    '#C0392B',
    '#1F7A3F',
    '#8A5A00',
    '#3E5C76',
] as const;

export const getShareMemberColor = (index: number) =>
    SHARE_MEMBER_COLORS[((index % SHARE_MEMBER_COLORS.length) + SHARE_MEMBER_COLORS.length) % SHARE_MEMBER_COLORS.length];

export type ShareMemberUsage = {
    memberId: string;
    name: string;
    isOwner: boolean;
    isDeleted: boolean;
    /**
     * 이름을 실제로 확인했는지. false면 화면에서 제거를 막는다 —
     * 누구를 빼는지 모른 채 되돌리기 어려운 동작을 실행하게 두지 않는다.
     */
    isNameResolved: boolean;
    usedCount: number;
    reservedCount: number;
    /** 이 회원이 가져간 회차 = 사용 완료 + 예약·대기 */
    takenCount: number;
    color: string;
};

export type ShareUsageBreakdown = {
    /** 총 회차. 확인할 수 없으면 null이고 이때 막대를 그리지 않는다. */
    capacity: number | null;
    usedCount: number | null;
    reservedCount: number | null;
    /** 지금 새로 예약할 수 있는 회차. */
    bookableRemaining: number | null;
    /** 사람별 분해가 가능한 풀인지. 그룹은 false. */
    hasMemberBreakdown: boolean;
    members: ShareMemberUsage[];
    /**
     * 사람별 합이 총 사용량과 어긋나 막대로 다 설명되지 않는 회차.
     * 참여자에서 빠진 사람이 남긴 사용 이력 등으로 생긴다. 0보다 크면 '기타'로 그린다.
     */
    unattributedCount: number;
};

const toCount = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : null;

const sumCounts = (values: Array<number | null>) =>
    values.reduce<number>((total, value) => total + (value ?? 0), 0);

const UNRESOLVED_NAME = '이름 확인 불가';

const toMemberUsage = (
    summary: Pick<SharedMembershipParticipantSummary, 'memberId' | 'name' | 'isDeleted' | 'usage'>,
    isOwner: boolean,
    colorIndex: number,
): ShareMemberUsage => {
    const usedCount = toCount(summary.usage?.usedCount) ?? 0;
    // reservedCount는 서버가 futureScheduled + pending을 합쳐 내려준다.
    // 없으면 두 값을 직접 더한다 — 레거시 응답에서 reservedCount만 빠지는 경우가 있다.
    const reservedCount =
        toCount(summary.usage?.reservedCount) ??
        sumCounts([
            toCount(summary.usage?.futureScheduledCount),
            toCount(summary.usage?.pendingRequestCount),
        ]);

    return {
        memberId: String(summary.memberId),
        name: summary.name || UNRESOLVED_NAME,
        isOwner,
        isDeleted: summary.isDeleted === true,
        isNameResolved: !!summary.name && summary.name !== UNRESOLVED_NAME,
        usedCount,
        reservedCount,
        takenCount: usedCount + reservedCount,
        color: getShareMemberColor(colorIndex),
    };
};

/**
 * 막대와 참여자 목록이 함께 쓸 분해값을 만든다.
 *
 * @param knownNames 응답 요약에 이름이 빠진 참여자를 화면이 기억해 둔 이름으로 메운다.
 */
export const buildShareUsageBreakdown = (
    pool: SharedMembershipPool | null | undefined,
    ownerFallbackName = '',
    knownNames: Record<string, string> = {},
): ShareUsageBreakdown => {
    const empty: ShareUsageBreakdown = {
        capacity: null,
        usedCount: null,
        reservedCount: null,
        bookableRemaining: null,
        hasMemberBreakdown: false,
        members: [],
        unattributedCount: 0,
    };
    if (!pool) return empty;

    const isGroup = isGroupSharePool(pool);
    const ownerId = String(pool.ownerMemberId || '');
    const summaryByMemberId = new Map(
        (pool.participantSummaries || []).map((summary) => [String(summary.memberId), summary]),
    );

    /**
     * 소유자를 항상 첫 줄에 둔다. 참여자는 서버가 준 순서(추가된 순서)를 지킨다 —
     * 사용량으로 정렬하면 새로고침마다 줄이 뒤바뀌어 어느 줄을 누르는지 헷갈린다.
     */
    const ownerSummary = pool.ownerSummary || {
        memberId: ownerId,
        name: ownerFallbackName,
    };
    const members: ShareMemberUsage[] = [
        toMemberUsage(
            { ...ownerSummary, name: ownerSummary.name || ownerFallbackName || '수강권 소유자' },
            true,
            0,
        ),
    ];
    (pool.participantMemberIds || []).forEach((participantId) => {
        const normalizedId = String(participantId);
        if (!normalizedId || normalizedId === ownerId) return;
        const summary = summaryByMemberId.get(normalizedId);
        const name = summary?.name || knownNames[normalizedId] || '';
        // 이름을 확인할 수 없는 참여자는 목록에서 빼는 대신 자리를 남긴다.
        // 인원 수만 맞고 줄이 없으면 "한 명이 사라졌다"로 읽힌다.
        members.push(
            toMemberUsage(
                {
                    memberId: normalizedId,
                    name,
                    isDeleted: summary?.isDeleted,
                    usage: summary?.usage,
                },
                false,
                members.length,
            ),
        );
    });

    if (isGroup) {
        // 그룹은 예약 확정 시 즉시 차감하므로 '남은 회차'가 곧 예약 가능 회차다.
        const capacity = toCount(pool.sourceMemberTicket?.totalSession);
        const bookableRemaining = getSharePoolRemainingCount(pool);
        return {
            capacity,
            usedCount:
                capacity !== null && bookableRemaining !== null
                    ? Math.max(0, capacity - bookableRemaining)
                    : null,
            reservedCount: null,
            bookableRemaining,
            hasMemberBreakdown: false,
            members,
            unattributedCount: 0,
        };
    }

    const memberUsedTotal = sumCounts(members.map((member) => member.usedCount));
    const memberReservedTotal = sumCounts(members.map((member) => member.reservedCount));
    const capacity = toCount(pool.usage?.capacity) ?? toCount(pool.sourceMembership?.totalSession);
    const usedCount = toCount(pool.usage?.usedCount) ?? memberUsedTotal;
    const reservedCount =
        toCount(pool.usage?.reservedCount) ??
        sumCounts([
            toCount(pool.usage?.futureScheduledCount),
            toCount(pool.usage?.pendingRequestCount),
        ]) ??
        memberReservedTotal;
    const bookableRemaining =
        toCount(pool.usage?.bookableRemaining) ??
        (capacity !== null ? Math.max(0, capacity - usedCount - reservedCount) : null);

    return {
        capacity,
        usedCount,
        reservedCount,
        bookableRemaining,
        hasMemberBreakdown: true,
        members,
        // 총계가 사람별 합보다 크면 그 차이가 막대에서 사라진다. 남는 만큼을 따로 잡아둔다.
        unattributedCount: Math.max(
            0,
            usedCount + reservedCount - (memberUsedTotal + memberReservedTotal),
        ),
    };
};

export type ShareUsageBarSegment = {
    key: string;
    label: string;
    count: number;
    /** 0~100. 총 회차 대비 너비. */
    percent: number;
    color: string;
    /** 예약 가능 구간은 회색 트랙으로 그린다. */
    isRemaining: boolean;
};

/**
 * 막대 세그먼트로 편다. 총 회차를 확인할 수 없거나 0이면 빈 배열 —
 * 이때 화면은 막대를 감추고 숫자만 보여준다.
 */
export const buildShareUsageBarSegments = (
    breakdown: ShareUsageBreakdown,
): ShareUsageBarSegment[] => {
    const capacity = breakdown.capacity;
    if (capacity === null || capacity <= 0) return [];

    const toPercent = (count: number) => (count / capacity) * 100;
    const segments: ShareUsageBarSegment[] = [];

    if (breakdown.hasMemberBreakdown) {
        breakdown.members.forEach((member) => {
            if (member.takenCount <= 0) return;
            segments.push({
                key: member.memberId,
                label: member.name,
                count: member.takenCount,
                percent: toPercent(member.takenCount),
                color: member.color,
                isRemaining: false,
            });
        });
        if (breakdown.unattributedCount > 0) {
            segments.push({
                key: 'unattributed',
                label: '참여자에서 제외된 사용',
                count: breakdown.unattributedCount,
                percent: toPercent(breakdown.unattributedCount),
                color: '#98A2B3',
                isRemaining: false,
            });
        }
    } else if ((breakdown.usedCount ?? 0) > 0) {
        const usedCount = breakdown.usedCount ?? 0;
        segments.push({
            key: 'used',
            label: '사용·예약 확정',
            count: usedCount,
            percent: toPercent(usedCount),
            color: getShareMemberColor(0),
            isRemaining: false,
        });
    }

    const takenPercent = segments.reduce((total, segment) => total + segment.percent, 0);
    const remaining = breakdown.bookableRemaining;
    if (remaining !== null && remaining > 0) {
        segments.push({
            key: 'remaining',
            label: '예약 가능',
            count: remaining,
            // 서버 총계와 사람별 합이 어긋나도 막대가 100%를 넘지 않게 남은 폭으로 자른다.
            percent: Math.max(0, Math.min(toPercent(remaining), 100 - takenPercent)),
            color: '#E3E8F0',
            isRemaining: true,
        });
    }

    return segments;
};
